import React, { useEffect, useState } from "react";
import {
  collection, query, where, getDocs, limit,
  addDoc, doc, updateDoc, increment,
  onSnapshot, getDoc, runTransaction, serverTimestamp
} from "firebase/firestore";
import { auth, db, functions, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import BackHomeButton from "../components/BackHomeButton";
import { onAuthStateChanged } from "firebase/auth";

import WorksheetRequestConversation from "../components/WorksheetRequestConversation";

const Alert_Summary = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loggedinuser, setLoggedinuser] = useState(auth.currentUser?.email || "");
  const navigate = useNavigate();

  const [worksheetRequests, setWorksheetRequests] = useState([]);
const [requestsLoading, setRequestsLoading] = useState(true);
const [requestsError, setRequestsError] = useState("");
const [requestActionError, setRequestActionError] = useState("");
const [busyRequestId, setBusyRequestId] = useState(null);
const [requestReload, setRequestReload] = useState(0);

useEffect(() => {
  let unsubscribeRequests = () => {};

  const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
    unsubscribeRequests();
    setWorksheetRequests([]);
    setRequestsError("");

    if (!user) {
      setRequestsLoading(false);
      return;
    }

    setRequestsLoading(true);

    const requestsQuery = query(
      collection(db, "Worksheet_Requests"),
      where("requestedTeacherId", "==", user.uid)
    );

    unsubscribeRequests = onSnapshot(
      requestsQuery,
      (snapshot) => {
        const requests = snapshot.docs
          .map((requestDoc) => ({
            ...requestDoc.data(),
            id: requestDoc.id,
          }))
          .sort(
            (a, b) =>
              (b.timestamp?.seconds || 0) -
              (a.timestamp?.seconds || 0)
          );

        setWorksheetRequests(requests);
        setRequestsLoading(false);
        setRequestsError("");
      },
      (error) => {
        console.error("Unable to load worksheet requests:", error);
        setRequestsError(
          "Unable to load worksheet requests. Please try again."
        );
        setRequestsLoading(false);
      }
    );
  });

  return () => {
    unsubscribeRequests();
    unsubscribeAuth();
  };
}, [requestReload]);

  // ✅ Added Shared_Quizzes to the fetch loop
  const alertCollections = ["Shared_Worksheets", "Shared_Quizzes"];

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        let allAlerts = [];

        // 1. Fetch Direct Shared Worksheets and Quizzes
        for (const colName of alertCollections) {
          try {
            // 🚨 REMOVED orderBy() here to prevent Firebase Missing Index crashes!
            // We sort the combined array in memory at the bottom anyway.
            const q = query(
              collection(db, colName),
              where("friendEmail", "==", user.email),
              limit(50)
            );
            const snapshot = await getDocs(q);
            const results = snapshot.docs.map((doc) => ({
              id: doc.id,
              source: colName, // Marks it as "Shared_Worksheets" or "Shared_Quizzes"
              ...doc.data(),
            }));
            allAlerts = [...allAlerts, ...results];
          } catch (err) {
            console.warn(`Could not fetch from ${colName}:`, err);
          }
        }

        // 2. Fetch User Subscriptions
        const subQuery = query(collection(db, "subscriptions"), where("subscriberId", "==", user.uid));
        const subSnap = await getDocs(subQuery);
        const subMap = {};
        subSnap.docs.forEach(doc => {
          const data = doc.data();
          subMap[data.creatorId] = data.timestamp?.seconds || 0;
        });
        const subscribedCreatorIds = Object.keys(subMap);

        // 3. Fetch Recent Uploads from Subscribed Creators
        let creatorAlerts = [];
        if (subscribedCreatorIds.length > 0) {
          for (let i = 0; i < subscribedCreatorIds.length; i += 10) {
            const chunk = subscribedCreatorIds.slice(i, i + 10);
            const uQuery = query(collection(db, "uploads"), where("uid", "in", chunk));
            const uSnap = await getDocs(uQuery);
            
            uSnap.docs.forEach(docSnap => {
              const data = docSnap.data();
              const uploadTimeSecs = data.uploadTime?.seconds || 0;
              const subTimeSecs = subMap[data.uid] || 0;
              
              // Only show if uploaded AFTER they subscribed
              if (uploadTimeSecs >= subTimeSecs) {
                creatorAlerts.push({
                  id: docSnap.id,
                  source: "Subscribed_Creator",
                  creatorId: data.uid,
                  creatorName: data.name || data.uploadedBy,
                  fileName: data.calculatedFileName || data.title || data.fileName || 'Untitled',
                  school: data.school || 'Unknown School',
                  city: data.city || 'Unknown City',
                  standard: data.standard || 'N/A',
                  subject: data.subject || 'N/A',
                  createdAt: data.uploadTime, 
                  fileUrl: data.fileUrl
                });
              }
            });
          }
        }

        // Merge and Sort newest to oldest
        allAlerts = [...allAlerts, ...creatorAlerts];
        allAlerts.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

        setAlerts(allAlerts.slice(0, 100)); // Cap at 100 to keep performance fast
      } catch (error) {
        console.error("Error fetching alerts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
  }, []);

  // ✅ MAGIC: Fixed logic to accept Quizzes (which lack a fileUrl)
  const markAlertAsRead = async (alert) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const trackerId = alert.fileUrl || alert.id; // Quizzes use their Document ID
      if (!trackerId) return;

      await addDoc(collection(db, 'Downloads_And_Generations'), {
        userId: user.uid,
        email: user.email,
        action: 'Viewed_Alert', 
        fileName: alert.fileName || `Shared Quiz: ${alert.subject || 'Mixed'}`,
        fileUrl: trackerId,
        subject: alert.subject || 'Unknown',
        type: alert.source === "Subscribed_Creator" ? 'Creator_Upload' : 
              alert.source === "Shared_Quizzes" ? 'Shared_Quiz' : 'Shared_Worksheet',
        standard: alert.standard || 'Unknown',
        device: 'Alerts Dashboard', 
        timestamp: new Date()
      });

      // Give the creator their +1 download/view stat
      if (alert.source === "Subscribed_Creator" && alert.id) {
        const uploadRef = doc(db, 'uploads', alert.id);
        await updateDoc(uploadRef, { downloads: increment(1) });
      }
    } catch (error) {
      console.error("Error clearing alert notification:", error);
    }
  };

  // ✅ Button 1 Action: Routes to PDF or Quiz appropriately
  const handleOpenAlert = async (alert) => {
    await markAlertAsRead(alert); // Clears the bell!

    if (alert.source === "Shared_Quizzes") {
      navigate('/SameQuiz', {
        state: {
          Quiz_Text: alert.Quiz_Text,
          subject: alert.subject,
          topic: alert.topic,
          format: alert.format,
          standard: alert.standard,
          lesson: alert.lesson,
          school: alert.school
        }
      });
    } else if (alert.fileUrl) {
      window.open(alert.fileUrl, "_blank");
    }
  };

  // Button 2 Action: Visit Storefront
  const handleVisitStorefront = async (alert) => {
    await markAlertAsRead(alert); 
    if (alert.source === "Subscribed_Creator") {
      navigate(`/CreatorProfile/${alert.creatorId}`);
    } else {
      navigate("/download-forms", {
        state: { type: "All Types", subject: "All Subjects", shared: true },
      });
    }
  };

const respondToRequest = async (request, action, extra = {}) => {
  if (busyRequestId) return;

  setBusyRequestId(request.id);
  setRequestActionError("");

  const requestRef = doc(db, "Worksheet_Requests", request.id);

  let actionClaimed = false;
  let requestSaved = false;

  try {
    const user = auth.currentUser;

    if (!user) {
      throw new Error("Please sign in again.");
    }

    // Recheck assignment and prevent simultaneous actions.
    const currentRequest = await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(requestRef);

      if (!snapshot.exists()) {
        throw new Error("This request no longer exists.");
      }

      const data = snapshot.data();

      if (data.requestedTeacherId !== user.uid) {
        throw new Error("This request is assigned to another teacher.");
      }

      if (!["Open", "Awaiting Information"].includes(data.status)) {
        throw new Error("This request has already been closed.");
      }

      if (data.actionInProgress) {
        throw new Error("An action is already being processed.");
      }

      transaction.update(requestRef, {
        actionInProgress: true,
        updatedAt: serverTimestamp(),
      });

      return data;
    });

    actionClaimed = true;

    const reference = currentRequest.caseId || request.id;

    const updates = {
      lastAction: action,
      lastActionBy: user.uid,
      updatedAt: serverTimestamp(),
      awaitingInformation: action === "information",
      notificationStatus: "Pending",
    };

    let worksheetTitle;
    let message;

    if (action === "upload") {
      const file = extra.file;

      if (!file) {
        throw new Error("Please select a PDF.");
      }

      const fileRef = ref(
        storage,
        `worksheet_request_deliveries/${user.uid}/` +
          `${request.id}/${crypto.randomUUID()}.pdf`
      );

      await uploadBytes(fileRef, file, {
        contentType: "application/pdf",
        contentDisposition: 'attachment; filename="worksheet.pdf"',
      });

      const downloadUrl = await getDownloadURL(fileRef);

      Object.assign(updates, {
        status: "Completed",
        resolution: "Your worksheet is ready to download.",
        worksheetStoragePath: fileRef.fullPath,
        worksheetDownloadUrl: downloadUrl,
        worksheetFileName: file.name,
        worksheetSizeBytes: file.size,
        completedAt: serverTimestamp(),
      });

      worksheetTitle = `Your worksheet is ready — ${reference}`;

      message =
        `Your worksheet for request ${reference} is ready. ` +
        "Please sign in to Eascode, open the Request Worksheet page, " +
        "and find this request under Your Requests. " +
        "Click Download Worksheet to get your PDF.";

    } else if (action === "deny") {
      Object.assign(updates, {
        status: "Denied",
        resolution:
          "The selected teacher is unable to prepare this worksheet at this time.",
        deniedBy: user.uid,
        deniedAt: serverTimestamp(),
      });

      worksheetTitle = `Update on your worksheet request — ${reference}`;

      message =
        `Thank you for submitting worksheet request ${reference}. ` +
        "Unfortunately, your selected teacher is unable to prepare it " +
        "at this time. We apologise for the inconvenience. " +
        "You are welcome to submit a new request to another teacher. " +
        "Please contact Eascode if you need assistance.";

    } else {
      throw new Error("Invalid request action.");
    }

    // Save the worksheet/status before sending the notification.
    await updateDoc(requestRef, {
      ...updates,
      actionInProgress: false,
    });

    requestSaved = true;

    /*
     * Existing email function inserts message into HTML.
     * Escape user-provided text before passing it.
     */
    const escapeHtml = (value) =>
      String(value).replace(/[&<>"']/g, (character) => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[character]));

    let emailSent = false;

    try {
      const recipientEmail =
        typeof currentRequest.userEmail === "string"
          ? currentRequest.userEmail.trim()
          : "";

      if (!/^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/.test(recipientEmail)) {
        throw new Error("The requester has no valid email address.");
      }

      const sendEmail = httpsCallable(functions, "sendWorksheetEmail");

      await sendEmail({
        friendName: "Worksheet requester",
        friendEmail: recipientEmail,
        worksheetTitle: escapeHtml(worksheetTitle),
        message: escapeHtml(message).replace(/\r?\n/g, "<br>"),
      });

      emailSent = true;
    } catch (emailError) {
      console.error("Request saved; email not confirmed:", emailError);
    }

    let trackingSaved = true;

    try {
      await updateDoc(requestRef, {
        notificationStatus: emailSent ? "Sent" : "Not confirmed",
        ...(emailSent
          ? { notificationSentAt: serverTimestamp() }
          : {}),
      });
    } catch (trackingError) {
      trackingSaved = false;
      console.error("Unable to save email status:", trackingError);
    }

    window.alert(
      (action === "upload"
        ? "Worksheet saved. The requester can download it from their request page."
        : "Request status updated.") +
      (emailSent
        ? " Email notification sent."
        : " Email notification could not be confirmed. Do not repeat the action; contact support.") +
      (!trackingSaved
        ? " Email tracking could not be updated."
        : "")
    );

  } catch (error) {
    console.error("Worksheet request action failed:", error);

    if (actionClaimed && !requestSaved) {
      try {
        await updateDoc(requestRef, {
          actionInProgress: false,
        });
      } catch (unlockError) {
        console.error("Unable to clear request lock:", unlockError);
      }
    }

    setRequestActionError(
      error.message || "Unable to complete the request action."
    );
  } finally {
    setBusyRequestId(null);
  }
};

const uploadRequestedWorksheet = async (request, file) => {
  if (!file || busyRequestId) return;

  setRequestActionError("");

  if (
    !file.name.toLowerCase().endsWith(".pdf") ||
    file.size === 0 ||
    file.size > 10 * 1024 * 1024
  ) {
    setRequestActionError("Choose a PDF file no larger than 10 MB.");
    return;
  }

  const confirmed = window.confirm(
    `Upload "${file.name}" and make it available for the requester to download?`
  );

  if (!confirmed) return;

  await respondToRequest(request, "upload", { file });
};

const denyWorksheetRequest = async (request) => {
  if (busyRequestId) return;

  if (!window.confirm(
    "Do you Really want to deny this worksheet request?"
  )) {
    return;
  }

  await respondToRequest(request, "deny");
};

const requestMoreInformation = (request) => {
  const input = document.getElementById(
    `request-message-${request.id}-teacher`
  );

  if (!input) return;

  const conversation = input.closest("details");

  if (conversation) {
    conversation.open = true;
  }

  input.focus({ preventScroll: true });
  input.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });
};

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-blue-600 font-bold">
        Loading Alerts...
      </div>
    );
  }

  return (
    <div className="alerts-page w-full min-w-0 max-w-5xl mx-auto px-3 py-6 sm:px-4 sm:py-8">
  <style>{`
    .alerts-page,
    .alerts-page *,
    .alerts-page *::before,
    .alerts-page *::after {
      box-sizing: border-box;
    }

    .alerts-page section,
    .alerts-page article,
    .alerts-page div {
      min-width: 0;
    }

    .alerts-page p,
    .alerts-page h2,
    .alerts-page h3,
    .alerts-page h4,
    .alerts-page span,
    .alerts-page strong {
      overflow-wrap: anywhere;
    }

    .alerts-page button {
      min-width: 0;
      max-width: 100%;
      white-space: normal;
      overflow-wrap: anywhere;
    }
  `}</style>
      <BackHomeButton />

      <h2 className="text-2xl font-bold text-gray-800 mb-6 mt-4">
        Your Alerts
      </h2>

      <section
  aria-labelledby="teacher-requests-heading"
  className="mb-8"
>
  <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
    <h3
      id="teacher-requests-heading"
      className="text-lg font-bold text-slate-900"
    >
      Worksheet requests for you
    </h3>

    {!requestsLoading && !requestsError && (
      <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
        {worksheetRequests.length} total
      </span>
    )}
  </div>

  {requestActionError && (
    <p
      role="alert"
      className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
    >
      {requestActionError}
    </p>
  )}

  {requestsLoading ? (
    <p role="status" className="text-sm text-slate-500">
      Loading worksheet requests...
    </p>
  ) : requestsError ? (
    <div
      role="alert"
      className="rounded-xl border border-red-200 bg-red-50 p-4"
    >
      <p className="text-sm text-red-700">{requestsError}</p>

      <button
        type="button"
        onClick={() => setRequestReload((value) => value + 1)}
        className="mt-3 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700"
      >
        Try again
      </button>
    </div>
  ) : worksheetRequests.length === 0 ? (
    <p className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
      You have no open worksheet requests.
    </p>
  ) : (
    <div className="space-y-4">
      {worksheetRequests.map((request) => {
        const isBusy = busyRequestId === request.id;

        const canTakeAction =
  ["Open", "Awaiting Information"].includes(request.status) &&
  !["Sending", "Delivery uncertain"].includes(
    request.notificationStatus
  );

const actionsDisabled = Boolean(busyRequestId) || !canTakeAction;

        const date = request.timestamp?.seconds
          ? new Date(
              request.timestamp.seconds * 1000
            ).toLocaleDateString(undefined, {
              day: "numeric",
              month: "short",
              year: "numeric",
            })
          : "Just now";

        return (
          <article
            key={request.id}
            className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm sm:p-6"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
                Worksheet request
              </span>

              <span className="text-xs text-slate-500">
                {date}
              </span>
            </div>

            <h4 className="mt-4 text-lg font-bold text-slate-900">
              {request.issueType || "Worksheet"}
            </h4>

            <p className="mt-1 break-words text-sm text-slate-600">
              Requester ID:{" "}
              <span className="font-medium">
                {request.uid || "Not available"}
              </span>
            </p>

            <p className="mt-1 text-xs text-slate-500">
              Request ID: {request.caseId || request.id}
            </p>

            <p className="mt-1 text-xs font-semibold text-blue-700">
              Status: {request.status}
            </p>

            <div className="mt-4 rounded-xl bg-slate-50 p-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                Worksheet requirements
              </p>

              <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700">
                {request.issueDescription || "No details provided."}
              </p>
            </div>

<div className="mt-5 grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-3">
  <div className="min-w-0">
    <input
      id={`worksheet-pdf-${request.id}`}
      type="file"
      accept=".pdf,application/pdf"
      className="hidden"
      disabled={actionsDisabled}
      onChange={(event) => {
        const file = event.target.files?.[0];
        event.target.value = "";

        if (file) {
          uploadRequestedWorksheet(request, file);
        }
      }}
    />

    <button
      type="button"
      disabled={actionsDisabled}
      onClick={() => {
        document
          .getElementById(`worksheet-pdf-${request.id}`)
          ?.click();
      }}
      className="min-h-[44px] w-full min-w-0 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-wait disabled:opacity-50"
    >
      Upload Worksheet
    </button>
  </div>

  <button
    type="button"
    disabled={actionsDisabled}
    onClick={() => denyWorksheetRequest(request)}
    className="min-h-[44px] min-w-0 rounded-xl border border-red-200 bg-white px-4 py-3 text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-wait disabled:opacity-50"
  >
    Deny Request
  </button>

  <button
    type="button"
    disabled={actionsDisabled}
    onClick={() => requestMoreInformation(request)}
    className="min-h-[44px] min-w-0 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800 transition hover:bg-amber-100 disabled:cursor-wait disabled:opacity-50"
  >
    Request more Information
  </button>
</div>

            {isBusy && (
              <p role="status" className="mt-3 text-sm text-slate-500">
                Processing...
              </p>
            )}

            <WorksheetRequestConversation
              request={request}
              viewerRole="teacher"
              disabled={Boolean(busyRequestId)}
            />
          </article>
        );
      })}
    </div>
  )}
</section>

      {alerts.length === 0 ? (
        <p className="text-gray-500">No new alerts. Subscribe to creators or ask friends to share worksheets with you!</p>
      ) : (
        <div className="space-y-4">
          {alerts.map((alert) => {
            const date = alert.createdAt?.seconds
              ? new Date(alert.createdAt.seconds * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
              : "Recent";

            const isCreatorAlert = alert.source === "Subscribed_Creator";
            const isQuizAlert = alert.source === "Shared_Quizzes";

            return (
              <div
                key={alert.id}
                className={`border rounded-xl shadow-sm p-4 transition duration-200 
                  ${isCreatorAlert ? 'bg-purple-50/30 border-purple-100 hover:shadow-purple-100' : 
                   isQuizAlert ? 'bg-emerald-50/30 border-emerald-100 hover:shadow-emerald-100' : 'bg-white hover:shadow-md'}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-2 mb-2 border-b pb-2 border-gray-100 sm:items-center">
                  <p className={`text-sm font-bold uppercase tracking-wide 
                    ${isCreatorAlert ? "text-purple-600" : isQuizAlert ? "text-emerald-600" : "text-blue-600"}`}>
                    {isCreatorAlert ? "✨ New Creator Upload" : isQuizAlert ? "🧠 Shared Quiz" : "📤 Shared Worksheet"}
                  </p>
                  <p className="text-xs font-medium text-gray-400">{date}</p>
                </div>

                <div className="text-sm text-gray-700 space-y-1.5">
                  {isCreatorAlert ? (
                    <>
                      <p>Creator <strong>{alert.creatorName}</strong> just uploaded a new worksheet!</p>
                      <p>📄 File: <span className="font-semibold text-gray-900">{alert.fileName}</span></p>
                      <p className="text-xs text-gray-500 mt-1">
                        🏫 {alert.school}, {alert.city} • Std {alert.standard} • {alert.subject}
                      </p>
                    </>
                  ) : isQuizAlert ? (
                    <>
                      <p>Shared by: <strong>{alert.sharedByEmail}</strong></p>
                      <p>📝 Format: <span className="font-semibold text-gray-900">{alert.format}</span></p>
                      <p className="text-xs text-gray-500 mt-1">
                        🏫 {alert.school} • Std {alert.standard} • {alert.subject} - {alert.topic}
                      </p>
                    </>
                  ) : (
                    <>
                      <p>Shared by: <strong>{alert.sharedByEmail}</strong></p>
                      <p>📄 File: <span className="font-semibold text-gray-900">{alert.fileName}</span></p>
                      <p className="text-xs text-gray-500 mt-1">
                        🏫 {alert.school}, {alert.city} • Std {alert.standard} • {alert.subject}
                      </p>
                    </>
                  )}
                  
                  {/* Shared Message Display */}
                  {alert.message && (
                    <p className="text-xs text-gray-600 italic bg-gray-50 p-2 rounded mt-2 border border-gray-100">
                      "{alert.message}"
                    </p>
                  )}
                </div>

                {/* ✅ Action Buttons */}
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-end">
                  
                  {/* Button 1: Start Quiz OR Open Worksheet */}
                  <button
                    onClick={() => handleOpenAlert(alert)}
                    className="text-xs font-bold px-4 py-2 rounded-full transition shadow-sm bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200"
                  >
                    {isQuizAlert ? "Start Quiz" : "Open Worksheet"}
                  </button>

                  {/* Button 2: Only show Storefront button if it's a creator alert */}
                  {isCreatorAlert && (
                    <button
                      onClick={() => handleVisitStorefront(alert)}
                      className="text-xs font-bold px-4 py-2 rounded-full transition shadow-sm bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      Visit Storefront
                    </button>
                  )}

                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Alert_Summary;